import * as Sentry from '@sentry/react'
import i18n, { InitOptions } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next'

import {
  CORE_NAMESPACES,
  DEFAULT_LANGUAGE,
  DEFAULT_NAMESPACE,
  isNamespace,
  type Namespace,
  SUPPORTED_LANGUAGES,
} from './namespaces'
import { coreResources, loadNamespaceResource } from './resources'

const languageDetector = new LanguageDetector()

languageDetector.addDetector({
  name: 'defaultLanguage',
  lookup: () => DEFAULT_LANGUAGE,
})

const MAX_RECOVERY_ATTEMPTS = 3

const recoveryAttempts = new Map<Namespace, number>()

function recoverMissingNamespace(namespace: string, key: string) {
  for (const candidate of [namespace, key.split('.')[0]]) {
    if (!isNamespace(candidate)) {
      continue
    }

    const attempts = recoveryAttempts.get(candidate) ?? 0

    if (attempts >= MAX_RECOVERY_ATTEMPTS) {
      continue
    }

    resetFailedNamespaces([candidate])

    if (i18n.hasLoadedNamespace(candidate)) {
      continue
    }

    recoveryAttempts.set(candidate, attempts + 1)

    if (import.meta.env.DEV) {
      console.warn(
        `[i18n] namespace "${candidate}" was used before being loaded (key "${key}"). ` +
          'Declare it on the matching route with withNamespaces/namespacesLoader.',
      )
    }

    i18n.loadNamespaces(candidate)
  }
}

const i18nConfig: InitOptions = {
  resources: coreResources,
  partialBundledLanguages: true,
  ns: [...CORE_NAMESPACES],
  defaultNS: DEFAULT_NAMESPACE,

  fallbackLng: false,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  load: 'currentOnly',

  nsSeparator: '.',
  keySeparator: '.',
  pluralSeparator: '_',

  saveMissing: true,
  missingKeyHandler: (_languages, namespace, key) => {
    recoverMissingNamespace(namespace, key)
  },

  detection: {
    order: [
      'navigator',
      'querystring',
      'cookie',
      'localStorage',
      'sessionStorage',
      'htmlTag',
      'path',
      'subdomain',
      'defaultLanguage',
    ],
    caches: [],
  },

  react: {
    bindI18nStore: 'added',
  },
}

const failedNamespaces = new Set<Namespace>()
const reportedFailures = new Set<Namespace>()

i18n.on('failedLoading', (language: string, namespace: string, message) => {
  if (!isNamespace(namespace)) {
    return
  }

  failedNamespaces.add(namespace)

  if (reportedFailures.has(namespace)) {
    return
  }

  reportedFailures.add(namespace)

  Sentry.captureException(
    new Error(
      `[i18n] failed to load namespace "${namespace}" for "${language}": ${message}`,
    ),
  )
})

export function resetFailedNamespaces(
  namespaces: readonly Namespace[],
): Namespace[] {
  const failed = namespaces.filter((namespace) =>
    failedNamespaces.has(namespace),
  )

  if (failed.length === 0) {
    return []
  }

  const { state } = i18n.services.backendConnector as unknown as {
    state: Record<string, number>
  }

  failed.forEach((namespace) => {
    failedNamespaces.delete(namespace)
    i18n.languages.forEach((language) => {
      delete state[`${language}|${namespace}`]
    })
  })

  return failed
}

i18n
  .use(languageDetector)
  .use(resourcesToBackend(loadNamespaceResource))
  .use(initReactI18next)
  .init(i18nConfig)

export default i18n
